import { Controller } from "@hotwired/stimulus"
import consumer from "../channels/consumer"
import { createLogger } from '../utils/logger'

// Connects to data-controller="execution-progress"
export default class extends Controller {
  static values = { 
    buildSessionId: String
  }
  
  static targets = [
    "statusBadge", "statusIcon", "statusText",
    "totalCount", "completedCount", "queueCount",
    "phaseProgress"
  ]
  
  connect() {
    this.logger = createLogger('execution-progress')
    this.logger.debug('Execution progress controller connected')
    this.setupWebSocket()
  }
  
  disconnect() {
    this.logger.debug('Execution progress controller disconnecting')
    if (this.subscription) {
      this.subscription.unsubscribe()
    }
  }
  
  setupWebSocket() {
    this.subscription = consumer.subscriptions.create(
      {
        channel: "BuildProgressChannel",
        build_session_id: this.buildSessionIdValue
      },
      {
        connected: () => {
          this.logger.debug("Execution progress WebSocket connected")
        },
        
        disconnected: () => {
          this.logger.debug("Execution progress WebSocket disconnected")
        },
        
        received: (data) => {
          this.handleWebSocketMessage(data)
        }
      }
    )
  }
  
  handleWebSocketMessage(data) {
    this.logger.debug("Execution progress received:", data.type)
    
    switch (data.type) {
      case 'automation_update':
        this.updateAutomationState(data.data)
        break
      case 'story_progress':
        this.updateStoryProgress(data)
        break
      case 'queue_status':
        this.updateQueueStatus(data.queue)
        break
      case 'automation_started':
      case 'automation_paused':
      case 'automation_resumed':
      case 'automation_stopped':
        this.updateStatus(data)
        break
      case 'progress_update':
        this.updateProgress(data)
        break
    }
  }
  
  updateAutomationState(data) {
    // Update status badge
    if (data.automation_state && this.hasStatusTextTarget) {
      this.statusTextTarget.textContent = this.humanizeState(data.automation_state)
      this.updateStatusBadgeClass(data.automation_state)
      this.updateStatusIcon(data.automation_state)
    }
    
    // Update queue count
    if (data.queue_size !== undefined && this.hasQueueCountTarget) {
      this.queueCountTarget.textContent = data.queue_size
    }
    
    // Update completed count if provided
    if (data.stories_completed !== undefined && this.hasCompletedCountTarget) {
      this.completedCountTarget.textContent = data.stories_completed
    }
  }
  
  updateStoryProgress(data) {
    // When a story completes, update counts
    if (data.status === 'completed' && this.hasCompletedCountTarget) {
      const currentCount = parseInt(this.completedCountTarget.textContent) || 0
      this.completedCountTarget.textContent = currentCount + 1
    }
    
    // Update queue count if the story was removed from queue
    if (data.data && data.data.queue_size !== undefined && this.hasQueueCountTarget) {
      this.queueCountTarget.textContent = data.data.queue_size
    }
  }
  
  updateQueueStatus(queueData) {
    if (this.hasQueueCountTarget && queueData.size !== undefined) {
      this.queueCountTarget.textContent = queueData.size
    }
    
    if (this.hasCompletedCountTarget && queueData.completed !== undefined) {
      this.completedCountTarget.textContent = queueData.completed
    }
  }
  
  updateStatus(data) {
    let state = 'idle'
    
    switch (data.type) {
      case 'automation_started':
        state = 'running'
        break
      case 'automation_paused':
        state = 'paused'
        break
      case 'automation_resumed':
        state = 'running'
        break
      case 'automation_stopped':
        state = 'idle'
        break
    }
    
    if (this.hasStatusTextTarget) {
      this.statusTextTarget.textContent = this.humanizeState(state)
      this.updateStatusBadgeClass(state)
      this.updateStatusIcon(state)
    }
  }
  
  updateProgress(data) {
    // Update progress percentage if we have phase progress bars
    if (data.progress !== undefined && this.hasPhaseProgressTarget) {
      // This would update phase-specific progress bars
      // For now, we'll just log it
      console.log('Progress update:', data.progress)
    }
    
    // Update message in activity feed if present
    if (data.message) {
      this.addActivityMessage(data.message)
    }
  }
  
  updateStatusBadgeClass(state) {
    if (!this.hasStatusBadgeTarget) return
    
    // Remove all badge color classes
    this.statusBadgeTarget.classList.remove('badge-success', 'badge-warning', 'badge-ghost')
    
    // Add appropriate class based on state
    switch (state) {
      case 'running':
        this.statusBadgeTarget.classList.add('badge-success')
        break
      case 'paused':
        this.statusBadgeTarget.classList.add('badge-warning')
        break
      default:
        this.statusBadgeTarget.classList.add('badge-ghost')
    }
  }
  
  updateStatusIcon(state) {
    if (!this.hasStatusIconTarget) return
    
    // Remove all icon classes
    this.statusIconTarget.className = ''
    
    // Add appropriate icon based on state
    switch (state) {
      case 'running':
        this.statusIconTarget.className = 'fas fa-play'
        break
      case 'paused':
        this.statusIconTarget.className = 'fas fa-pause'
        break
      default:
        this.statusIconTarget.className = 'fas fa-circle'
    }
  }
  
  humanizeState(state) {
    switch (state) {
      case 'running':
        return 'Running'
      case 'paused':
        return 'Paused'
      case 'idle':
        return 'Idle'
      case 'stopped':
        return 'Stopped'
      default:
        return state.charAt(0).toUpperCase() + state.slice(1)
    }
  }
  
  addActivityMessage(message) {
    const activityFeed = document.getElementById('activity-feed')
    if (!activityFeed) return
    
    // Remove "waiting" message if present
    const waitingMsg = activityFeed.querySelector('.italic')
    if (waitingMsg) {
      waitingMsg.remove()
    }
    
    // Create new activity message
    const messageDiv = document.createElement('div')
    messageDiv.className = 'text-xs text-base-content/80'
    messageDiv.innerHTML = `
      <span class="text-base-content/60">${new Date().toLocaleTimeString()}</span>
      <span class="ml-2">${message}</span>
    `
    
    // Add to feed
    activityFeed.appendChild(messageDiv)
    
    // Scroll to bottom
    activityFeed.scrollTop = activityFeed.scrollHeight
    
    // Limit to 50 messages
    while (activityFeed.children.length > 50) {
      activityFeed.removeChild(activityFeed.firstChild)
    }
  }
}